import type { AddressSearchProvider } from 'api/models/mainservice';
import { MAIN_API_ROOT } from 'app_config';
import type { Codebook } from 'common/models';
import {
  DateInput,
  PhoneInput,
  RadioGroupInput,
  SelectInput,
  TextAreaInput,
  TextInput
} from 'components/forms/hook-form';
import DraggerUploadInput from 'components/forms/hook-form/ListUploadInput';
import dayjs from 'dayjs';
import { getCustomerKindTranslateKey } from 'helpers/customerHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import { useValidators } from 'helpers/validation';
import { CustomerKindArray } from 'pages/customers/models';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import type { PublicOrderRequestFormValues } from './PublicOrderRequestForm';
import PublicOrderRequestFormAddressHandler from './PublicOrderRequestFormAddressHandler';

interface PublicOrderRequestFormFieldsProps {
  hideKindSwitch?: boolean;
  categories: Codebook[];
  orderRequestFormRequestedDateEnabled?: boolean;
  requestedDateNote?: string | null;
  orderRequestFormServiceObjectInfoEnabled?: boolean;
  tenantCode: string;
  addressSearchProvider: AddressSearchProvider;
}

const PublicOrderRequestFormFields = ({
  hideKindSwitch,
  categories,
  orderRequestFormRequestedDateEnabled,
  requestedDateNote,
  orderRequestFormServiceObjectInfoEnabled,
  tenantCode,
  addressSearchProvider
}: PublicOrderRequestFormFieldsProps) => {
  const { tInput, tCustomer, tServiceObject, tRequest } = useCustomTranslation();
  const validators = useValidators();

  const { watch } = useFormContext<PublicOrderRequestFormValues>();

  const isCompany = watch('customerKind') === 'Company';
  return (
    <>
      {!hideKindSwitch && (
        <RadioGroupInput
          items={CustomerKindArray.map((kind) => ({
            label: tCustomer(getCustomerKindTranslateKey(kind)),
            value: kind
          }))}
          name="customerKind"
          required
          buttonStyle="solid"
        />
      )}
      <div className="PublicOrderRequestForm_box first">
        {isCompany && (
          <>
            <TextInput
              name="customerName"
              label={tInput('company_name_input')}
              placeholder={tInput('company_name_placeholder')}
              required
              options={{
                required: validators.required,
                maxLength: validators.maxLength(100)
              }}
            />
            <TextInput
              name="identificationNumber"
              label={tCustomer('identification_number')}
              placeholder={tCustomer('identification_number_placeholder')}
              required
              options={{
                required: validators.required,
                maxLength: validators.maxLength(50)
              }}
            />
          </>
        )}
        <TextInput
          name="firstName"
          label={tInput('first_name_input')}
          placeholder={tInput('first_name_placeholder')}
          required
          options={{
            required: validators.required,
            maxLength: validators.maxLength(49)
          }}
        />
        <TextInput
          name="lastName"
          label={tInput('last_name_input')}
          placeholder={tInput('last_name_placeholder')}
          required
          options={{
            required: validators.required,
            maxLength: validators.maxLength(49)
          }}
        />
        <PhoneInput<PublicOrderRequestFormValues>
          name="phone"
          required
          label={tInput('phone_input')}
          placeholder={tInput('phone_placeholder')}
        />
        <TextInput
          name="email"
          label={tInput('email_input')}
          placeholder={tInput('email_contact_placeholder')}
          required
          type="email"
          options={{
            required: validators.required
          }}
        />
      </div>
      <div className="PublicOrderRequestForm_box">
        <div className="column">
          <SelectInput
            name="categoryId"
            required
            options={{ required: validators.required }}
            items={categories}
            label={tInput('category_input')}
            placeholder={tInput('category_placeholder')}
          />
          {orderRequestFormServiceObjectInfoEnabled && (
            <div className="PublicOrderRequestForm_box-row">
              <TextInput
                name="serviceObjectName"
                label={tServiceObject('name')}
                placeholder={tServiceObject('name_placeholder')}
                options={{ maxLength: validators.maxLength(50) }}
              />
              <TextInput
                name="serviceObjectIdentificationNumber"
                label={tServiceObject('identification_number')}
                placeholder={tServiceObject('identification_number_placeholder')}
                options={{ maxLength: validators.maxLength(50) }}
              />
            </div>
          )}
          <TextAreaInput
            name="description"
            label={tRequest('description')}
            placeholder={tRequest('description_placeholder')}
            minRows={5}
          />
        </div>
        <div className="column">
          <PublicOrderRequestFormAddressHandler addressSearchProvider={addressSearchProvider} />
          {orderRequestFormRequestedDateEnabled && (
            <DateInput
              name="requestedDate"
              disabledDate={(current: dayjs.Dayjs) => current && current < dayjs.utc().endOf('day')}
              required
              options={{ required: validators.required }}
              label={tRequest('requested_date')}
              placeholder={tRequest('requested_date_placeholder')}
              inputNote={{
                value: requestedDateNote || tRequest('requested_date_input_note'),
                mode: 'inline'
              }}
            />
          )}
          <DraggerUploadInput
            name="attachments"
            label={tInput('attachments_input')}
            uploadPath={`${MAIN_API_ROOT}/public/attachment/${tenantCode}`}
            multiple
          />
        </div>
      </div>
    </>
  );
};

export default PublicOrderRequestFormFields;
